import React, {useEffect, useRef, useState} from 'react'
import {Box, Stack, Typography, useTheme} from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import AppDialog from 'src/components-new/feedback/AppDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import MultiSelectNew from 'src/components-new/inputs/MultiSelectNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'

/**
 * New-style port of src/components/templates/Uploader/Uploader.tsx, scoped to
 * the patient-detail Documents tab. Single self-contained component: the
 * trigger button, the staged-file list and the upload/remove actions all
 * live here — the parent (DocumentsTab, wired up in a follow-up task) only
 * needs to supply the two callback props below.
 */

interface StagedFile {
  id: string
  file: File
  category: string[]
  name: string
}

interface DocumentUploaderNewProps {
  onSubmitFile: (
    file: File,
    metadata: {category: string[]; name?: string},
    setStatus: (status: string) => void
  ) => Promise<void>
  onSubmitRefresh: () => void
}

let stagedFileSeq = 0
function nextStagedFileId(): string {
  stagedFileSeq += 1
  return `staged-file-${Date.now()}-${stagedFileSeq}`
}

function formatSize(size: number): string {
  return `${Math.round((size / (1024 ** 2)) * 100) / 100} MB`
}

export default function DocumentUploaderNew({onSubmitFile, onSubmitRefresh}: DocumentUploaderNewProps): JSX.Element {
  const theme = useTheme()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const documentsConfigurations = useAppSelector((state) => state.pspConfigReducer.data.documentsConfigurations)
  const categoryOptions: {value: string; label: string}[] = documentsConfigurations?.categoryOptions?.categoriesUpload ?? []

  const [open, setOpen] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([])

  const [status, setStatus] = useState<string | undefined>(undefined)
  const [pendingUpload, setPendingUpload] = useState<{id: string; name: string} | null>(null)
  const [rowLoadingId, setRowLoadingId] = useState<string | null>(null)
  const [uploadingAll, setUploadingAll] = useState(false)
  const [confirmFileId, setConfirmFileId] = useState<string | null>(null)

  const confirmFile = stagedFiles.find((f) => f.id === confirmFileId) ?? null

  // Mirrors the setStatus-driven pattern used elsewhere in components-new
  // (e.g. ActivityDocumentsTabNew): onSubmitFile reports PENDING/FULFILLED/
  // REJECTED asynchronously via the callback, so we react to it here rather
  // than trying to read a return value.
  useEffect(() => {
    if (status === STATUS.FULFILLED && pendingUpload && !uploadingAll) {
      setStagedFiles((prev) => prev.filter((f) => f.id !== pendingUpload.id))
      onSubmitRefresh()
      setPendingUpload(null)
      setRowLoadingId(null)
      setStatus(undefined)
    }
    if (status === STATUS.REJECTED) {
      setRowLoadingId(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  const addFiles = (files: FileList | File[]) => {
    const incoming: StagedFile[] = Array.from(files).map((file) => ({
      id: nextStagedFileId(),
      file,
      category: [],
      name: file.name,
    }))
    if (incoming.length === 0) return
    setStagedFiles((prev) => [...prev, ...incoming])
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files)
  }

  const updateCategory = (id: string, category: string[]) => {
    setStagedFiles((prev) => prev.map((f) => (f.id === id ? {...f, category} : f)))
  }

  const updateName = (id: string, name: string) => {
    setStagedFiles((prev) => prev.map((f) => (f.id === id ? {...f, name} : f)))
  }

  const removeStagedFile = (id: string) => {
    setStagedFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const clearAll = () => setStagedFiles([])

  const handleConfirmUpload = async () => {
    if (!confirmFile) return
    const {id, name} = confirmFile
    setConfirmFileId(null)
    setPendingUpload({id, name})
    setRowLoadingId(id)
    setStatus(undefined)
    await onSubmitFile(confirmFile.file, {category: confirmFile.category, name: confirmFile.name}, setStatus)
  }

  const handleUploadAll = async () => {
    setUploadingAll(true)
    setStatus(undefined)
    for (const staged of stagedFiles) {
      setPendingUpload({id: staged.id, name: staged.name})
      // eslint-disable-next-line no-await-in-loop
      await onSubmitFile(staged.file, {category: staged.category, name: staged.name}, setStatus)
    }
    setUploadingAll(false)
    setPendingUpload(null)
    await onSubmitRefresh()
    clearAll()
  }

  const canUploadAll = stagedFiles.length > 0 && stagedFiles.every((f) => f.category.length > 0)

  const handleClose = () => {
    setOpen(false)
    setConfirmFileId(null)
  }

  return (
    <>
      <Button variant="primary" startIcon={<CloudUploadOutlinedIcon sx={{fontSize: 18}} />} onClick={() => setOpen(true)}>
        {'Carica documento'}
      </Button>

      <AppDialog
        open={open}
        onClose={handleClose}
        title="Carica documento"
        maxWidth="md"
        disableEnforceFocus={confirmFileId !== null}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={clearAll}
              disabled={stagedFiles.length === 0 || uploadingAll}
            >
              {'Rimuovi tutto'}
            </Button>
            <Button
              variant="primary"
              onClick={handleUploadAll}
              loading={uploadingAll}
              disabled={!canUploadAll || uploadingAll}
            >
              {'Carica tutto'}
            </Button>
          </>
        }
      >
        <Stack spacing={2}>
          {status === STATUS.REJECTED && (
            <InlineMessage severity="error" onClose={() => setStatus(undefined)}>
              {pendingUpload
                ? `Il file "${pendingUpload.name}" non è stato caricato.`
                : 'Caricamento del file non riuscito.'}
            </InlineMessage>
          )}

          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            sx={{
              border: `2px dashed ${isDragOver ? theme.palette.primary.main : NEUTRAL_SCALE[300]}`,
              borderRadius: '12px',
              backgroundColor: isDragOver ? theme.palette.primary.light : NEUTRAL_SCALE[50],
              padding: 3,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.15s, border-color 0.15s',
            }}
          >
            <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileInputChange} />
            <CloudUploadOutlinedIcon sx={{fontSize: 32, color: NEUTRAL_SCALE[500], mb: 1}} />
            <Typography variant="body2" sx={{color: NEUTRAL_SCALE[700]}}>
              {'Clicca o trascina qui i file da caricare'}
            </Typography>
          </Box>

          {stagedFiles.length > 0 && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={2} sx={{px: 1, display: {xs: 'none', sm: 'flex'}}}>
                <Typography variant="caption" sx={{flex: '1 1 20%', fontWeight: 600, color: NEUTRAL_SCALE[500], textTransform: 'uppercase'}}>{'File'}</Typography>
                <Typography variant="caption" sx={{flex: '1 1 30%', fontWeight: 600, color: NEUTRAL_SCALE[500], textTransform: 'uppercase'}}>{'Categoria'}</Typography>
                <Typography variant="caption" sx={{flex: '1 1 25%', fontWeight: 600, color: NEUTRAL_SCALE[500], textTransform: 'uppercase'}}>{'Nome'}</Typography>
                <Typography variant="caption" sx={{width: 72, fontWeight: 600, color: NEUTRAL_SCALE[500], textTransform: 'uppercase'}}>{'Dim.'}</Typography>
                <Box sx={{width: 80}} />
              </Stack>

              {stagedFiles.map((staged) => {
                const canUploadRow = staged.category.length > 0
                const rowBusy = rowLoadingId === staged.id || uploadingAll
                return (
                  <Stack
                    key={staged.id}
                    direction={{xs: 'column', sm: 'row'}}
                    spacing={{xs: 1, sm: 2}}
                    alignItems={{xs: 'stretch', sm: 'center'}}
                    sx={{p: 1.5, border: `1px solid ${NEUTRAL_SCALE[200]}`, borderRadius: '8px'}}
                  >
                    <Typography variant="body2" sx={{flex: '1 1 20%', wordBreak: 'break-word'}}>
                      {staged.file.name}
                    </Typography>
                    <Box sx={{flex: '1 1 30%'}}>
                      <MultiSelectNew
                        value={staged.category}
                        onChange={(value) => updateCategory(staged.id, value)}
                        options={categoryOptions}
                        disabled={rowBusy}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{flex: '1 1 25%'}}>
                      <TextFieldNew
                        value={staged.name}
                        onChange={(value) => updateName(staged.id, value)}
                        disabled={rowBusy}
                        fullWidth
                      />
                    </Box>
                    <Typography variant="body2" sx={{width: {xs: 'auto', sm: 72}, color: NEUTRAL_SCALE[500]}}>
                      {formatSize(staged.file.size)}
                    </Typography>
                    <Stack direction="row" sx={{width: {xs: 'auto', sm: 80}, justifyContent: {xs: 'flex-end', sm: 'flex-start'}}}>
                      <ActionIconButton
                        variant="download"
                        tooltip={canUploadRow ? 'Carica' : 'Inserisci categoria per caricare il file'}
                        disabled={!canUploadRow || rowBusy}
                        onClick={() => setConfirmFileId(staged.id)}
                      />
                      <ActionIconButton
                        variant="delete"
                        tooltip="Cancella"
                        disabled={rowBusy}
                        onClick={() => removeStagedFile(staged.id)}
                      />
                    </Stack>
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Stack>

        <AppDialog
          open={confirmFile !== null}
          onClose={() => setConfirmFileId(null)}
          title="Conferma caricamento"
          maxWidth="xs"
          actions={
            <>
              <Button variant="secondary" onClick={() => setConfirmFileId(null)}>
                {'Annulla'}
              </Button>
              <Button variant="primary" onClick={handleConfirmUpload} loading={rowLoadingId === confirmFile?.id}>
                {'Ok'}
              </Button>
            </>
          }
        >
          <Typography variant="body2">
            {`Vuoi caricare il file "${confirmFile?.name}"?`}
          </Typography>
        </AppDialog>
      </AppDialog>
    </>
  )
}
